import React, { useCallback, useEffect, useRef, useState } from 'react';
import trophyIcon from '../../assets/investor-pricing/trophy.png';
import checkIcon from '../../assets/investor-pricing/check.png';
import beyondUnderline from '../../assets/investor-pricing/beyond-underline.png';
import InvestorGuaranteeShield from './InvestorGuaranteeShield';
import {
  INVESTOR_GUARANTEE_LABEL,
  INVESTOR_PRICING_FEATURES,
  INVESTOR_PRICING_PLANS,
  INVESTOR_SUCCESS_FEE,
  type InvestorPlanId,
} from '../../constants/investorPricingPlans';
import { SUCCESS_FEE_LIVE } from '../../constants/successFee';

const PLAN_IDS = ['yearly', 'threeYear'] as const;
const MOBILE_CAROUSEL_MQ = '(max-width: 639px)';

interface InvestorPricingPlansProps {
  hideSuccessFee: boolean;
  onSelectPlan: (plan: InvestorPlanId) => void;
}

const CYAN = 'rgba(1, 176, 239, 0.85)';
const CYAN_SOLID = '#02b1f0';
/** Figma: original PRAIRI blue #01A7E3 at 85% opacity so white button type stands out. */
const CYAN_BUTTON = 'rgba(1, 167, 227, 0.85)';
const OLIVE = 'rgba(111, 155, 65, 1)';
const TEXT = '#f2f2f2';

const SELECT_CLASS =
  'w-full rounded-md px-3 py-2 text-center text-base font-medium text-white sm:px-4 sm:py-2.5';

function BeyondUnderline() {
  return (
    <span className="relative inline-block">
      beyond
      <img
        src={beyondUnderline}
        alt=""
        aria-hidden
        className="pointer-events-none absolute left-[-0.02em] w-[108%] max-w-none select-none object-fill object-left"
        style={{ bottom: '-0.04em', height: '0.15em' }}
      />
    </span>
  );
}

function FeatureRow({ label }: { label: string }) {
  return (
    <li
      className="flex items-start gap-2.5 text-[18px] leading-snug sm:gap-2 sm:text-[19px] sm:leading-tight"
      style={{ color: TEXT }}
    >
      <img
        src={checkIcon}
        alt="Included"
        className="mt-0.5 h-5 w-5 flex-shrink-0 object-contain sm:h-4 sm:w-4"
      />
      <span>{label}</span>
    </li>
  );
}

function SuccessFeeCallout() {
  return (
    <div
      className="flex items-center gap-3 rounded-xl px-3 py-2 sm:gap-2 sm:px-2.5 sm:py-2"
      style={{ border: `1px solid ${CYAN_SOLID}` }}
    >
      <img
        src={trophyIcon}
        alt="Success fee"
        className="h-9 w-9 flex-shrink-0 object-contain sm:h-8 sm:w-8"
      />
      <div className="min-w-0 flex-1">
        <p
          className="m-0 text-[17px] font-semibold leading-tight sm:text-[15px] sm:whitespace-nowrap"
          style={{ color: TEXT }}
        >
          {INVESTOR_SUCCESS_FEE.heading}
        </p>
        <p
          className="mt-1 text-[16px] font-light leading-snug sm:mt-0.5 sm:text-[14px] sm:leading-tight md:text-[17px]"
          style={{ color: 'rgba(242, 242, 242, 0.9)' }}
        >
          {INVESTOR_SUCCESS_FEE.body}
        </p>
      </div>
    </div>
  );
}

function GuaranteeRow() {
  return (
    <div
      className="flex items-center justify-center gap-2 text-[14px] sm:text-base"
      style={{ color: CYAN_BUTTON }}
    >
      <InvestorGuaranteeShield className="h-5 w-5" />
      <span>{INVESTOR_GUARANTEE_LABEL}</span>
    </div>
  );
}

function SelectButton({
  label,
  olive,
  onClick,
}: {
  label: string;
  olive?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={SELECT_CLASS}
      style={{
        backgroundColor: olive ? OLIVE : CYAN_BUTTON,
        border: `1px solid ${olive ? '#709b40' : CYAN_BUTTON}`,
        fontFamily: 'inherit',
      }}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

function PlanCard({
  planId,
  hideSuccessFee,
  hideTopSelect,
  onSelect,
}: {
  planId: InvestorPlanId;
  hideSuccessFee: boolean;
  hideTopSelect: boolean;
  onSelect: () => void;
}) {
  const plan = INVESTOR_PRICING_PLANS[planId];
  const isThreeYear = planId === 'threeYear';
  return (
    <article
      className="relative flex h-full min-w-0 flex-col rounded-[20px] border-2 bg-[#262626] p-4 pt-8 text-left sm:grid sm:grid-rows-subgrid sm:row-span-12 sm:p-5 sm:pt-8 md:p-6 md:pt-8 lg:border-4"
      style={{ borderColor: CYAN }}
    >
      <div className="relative">
        {plan.bestValue && (
          <div
            className="pointer-events-none absolute left-1/2 z-10 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded-[12px] px-5 py-1.5 text-[16px] font-bold tracking-wide sm:text-[18px]"
            style={{ top: '-2rem', backgroundColor: OLIVE, color: TEXT }}
          >
            BEST VALUE
          </div>
        )}
        <h3
          className="text-[24px] font-bold leading-tight sm:text-[24px] lg:text-[26px]"
          style={{ color: CYAN }}
        >
          {plan.name}
        </h3>
        <p
          className="mt-3 text-[18px] font-light leading-snug sm:text-[18px] sm:leading-snug"
          style={{ color: TEXT }}
        >
          <span className="block font-semibold">Why choose:</span>
          <span className="block">{plan.whyChoose.replace(/^Why choose:\s*/, '')}</span>
        </p>
      </div>

      <div className="mt-1.5 h-px w-full bg-[#F2F2F2]" />

      <div className="mt-5 sm:mt-6">
        {plan.compareAtPrice ? (
          <div
            className="text-[18px] font-light leading-tight sm:text-[20px]"
            style={{ color: TEXT }}
          >
            <span className="line-through decoration-red-500 decoration-2">
              {plan.compareAtPrice}
            </span>
          </div>
        ) : (
          <div
            className="invisible select-none text-[18px] font-light leading-tight sm:text-[20px]"
            aria-hidden
          >
            $2,700
          </div>
        )}
      </div>

      <div className="flex min-h-[42px] items-end gap-x-2.5 sm:min-h-0 sm:gap-x-3">
        <span
          className="shrink-0 text-[32px] font-medium leading-none sm:text-[36px] sm:font-semibold"
          style={{ color: TEXT }}
        >
          {plan.price}
        </span>
        <span
          className="min-w-0 pb-0.5 text-[16px] font-light leading-[1.25] sm:text-[17px]"
          style={{ color: TEXT }}
        >
          {plan.priceNoteLines ? (
            <>
              <span className="block whitespace-nowrap">{plan.priceNoteLines[0]}</span>
              <span className="block whitespace-nowrap">{plan.priceNoteLines[1]}</span>
            </>
          ) : (
            plan.priceNote
          )}
        </span>
      </div>

      <div className="mt-2.5 flex min-h-[22px] flex-wrap items-center gap-x-2.5 gap-y-1.5 sm:mt-3 sm:min-h-0 sm:gap-x-3">
        {plan.saveBadge ? (
          <span
            className="inline-flex shrink-0 items-center rounded-[6px] px-2.5 py-1 text-[13px] font-medium leading-none whitespace-nowrap sm:text-[14px]"
            style={{ backgroundColor: OLIVE, color: TEXT }}
          >
            {plan.saveBadge}
          </span>
        ) : null}
        {plan.equivalent ? (
          <span
            className="whitespace-nowrap text-[18px] font-light leading-none sm:text-[17px]"
            style={{ color: TEXT }}
          >
            {plan.equivalent}
          </span>
        ) : null}
      </div>

      <div className={hideTopSelect ? 'mt-2 sm:mt-2' : 'mt-5 sm:mt-6'}>
        {!hideTopSelect ? <SelectButton label={plan.selectLabel} onClick={onSelect} /> : null}
      </div>

      <div
        className={`h-px w-full bg-[#F2F2F2] ${
          hideTopSelect ? 'mt-2 sm:mt-2.5' : 'mt-3 sm:mt-3.5'
        }`}
      />

      <p
        className="mt-5 text-[21px] font-medium leading-snug sm:text-[22px]"
        style={{ color: CYAN }}
      >
        {plan.featuresHeading}
      </p>

      <ul className="mt-1.5 space-y-2 sm:mt-1.5 sm:space-y-1">
        {INVESTOR_PRICING_FEATURES.map(feature => (
          <FeatureRow key={feature} label={feature} />
        ))}
      </ul>

      <div className={hideSuccessFee ? '' : 'mt-5'}>
        {!hideSuccessFee ? <SuccessFeeCallout /> : null}
      </div>

      <div className={plan.plusHeading ? (hideSuccessFee ? 'mt-2' : 'mt-5') : ''}>
        {plan.plusHeading && plan.plusFeatures ? (
          <>
            <p
              className="text-[21px] font-medium leading-snug sm:text-[22px]"
              style={{ color: CYAN }}
            >
              {plan.plusHeading}
            </p>
            <ul className="mt-1.5 space-y-2 sm:mt-1.5 sm:space-y-1">
              {plan.plusFeatures.map(feature => (
                <FeatureRow key={feature} label={feature} />
              ))}
            </ul>
          </>
        ) : null}
      </div>

      <div className="mt-auto flex flex-col gap-3 pt-6 md:mt-0">
        <SelectButton label={plan.selectLabel} olive={isThreeYear} onClick={onSelect} />
        <GuaranteeRow />
      </div>
    </article>
  );
}

const InvestorPricingPlans: React.FC<InvestorPricingPlansProps> = ({
  hideSuccessFee,
  onSelectPlan,
}) => {
  const hideFee = hideSuccessFee || !SUCCESS_FEE_LIVE;
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const slideOffsetLeft = (scroller: HTMLDivElement, slide: HTMLElement) =>
    slide.getBoundingClientRect().left -
    scroller.getBoundingClientRect().left +
    scroller.scrollLeft;

  const updateActiveIndex = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller || !window.matchMedia(MOBILE_CAROUSEL_MQ).matches) {
      return;
    }
    const slides = Array.from(scroller.children) as HTMLElement[];
    if (slides.length === 0) {
      return;
    }
    const midpoint = scroller.scrollLeft + scroller.clientWidth / 2;
    let closest = 0;
    let closestDistance = Number.POSITIVE_INFINITY;
    slides.forEach((slide, index) => {
      const center = slideOffsetLeft(scroller, slide) + slide.offsetWidth / 2;
      const distance = Math.abs(center - midpoint);
      if (distance < closestDistance) {
        closestDistance = distance;
        closest = index;
      }
    });
    setActiveIndex(closest);
  }, []);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) {
      return;
    }
    updateActiveIndex();
    scroller.addEventListener('scroll', updateActiveIndex, { passive: true });
    window.addEventListener('resize', updateActiveIndex);
    return () => {
      scroller.removeEventListener('scroll', updateActiveIndex);
      window.removeEventListener('resize', updateActiveIndex);
    };
  }, [updateActiveIndex]);

  const scrollToPlan = (index: number) => {
    const scroller = scrollerRef.current;
    const slide = scroller?.children[index] as HTMLElement | undefined;
    if (!scroller || !slide) {
      return;
    }
    scroller.scrollTo({ left: slideOffsetLeft(scroller, slide), behavior: 'smooth' });
  };

  return (
    <section
      className="investor-native-plans flex w-full min-w-0 flex-col items-center text-left"
      style={{ fontFamily: 'Rubik, sans-serif' }}
    >
      <div className="w-full max-w-[850px] [container-type:inline-size]">
        <header className="mb-3 w-full md:mb-4">
          <h1 className="w-full font-normal leading-[1.2] text-[#faf2fa]">
            {/* Mobile: Unlock high-potential / Startups faster & / beyond your network */}
            <span className="block w-full text-center text-[clamp(24px,8.3cqi,28px)] leading-[1.2] sm:hidden">
              <span className="block whitespace-nowrap">Unlock high-potential</span>
              <span className="block whitespace-nowrap">
                Startups <span className="font-bold italic">faster</span> &amp;
              </span>
              <span className="block whitespace-nowrap">
                <BeyondUnderline /> your network
              </span>
            </span>
            {/* Tablet: 2 lines, same break as desktop */}
            <span className="hidden w-full text-center text-[32px] leading-[1.2] sm:block lg:hidden">
              <span className="block whitespace-nowrap">Unlock high-potential Startups</span>
              <span className="block whitespace-nowrap">
                <span className="font-bold italic">faster</span> &amp; <BeyondUnderline /> your
                network
              </span>
            </span>
            {/* Desktop: 2 lines, second line starts with faster — spans both plan cards */}
            <span className="hidden w-full text-center text-[38px] leading-[1.2] lg:block">
              <span className="block whitespace-nowrap">Unlock high-potential Startups</span>
              <span className="block whitespace-nowrap">
                <span className="font-bold italic">faster</span> &amp; <BeyondUnderline /> your
                network
              </span>
            </span>
          </h1>
        </header>

        <div
          className="mb-1 flex items-center justify-center gap-2 sm:hidden"
          role="tablist"
          aria-label="Plan slides"
        >
          {PLAN_IDS.map((planId, index) => {
            const isActive = activeIndex === index;
            return (
              <button
                key={planId}
                type="button"
                role="tab"
                aria-label={`Show ${INVESTOR_PRICING_PLANS[planId].name}`}
                aria-selected={isActive}
                className={`h-2 rounded-full transition-all ${
                  isActive ? 'w-6 bg-[#02b1f0]' : 'w-2 bg-white/30'
                }`}
                onClick={() => scrollToPlan(index)}
              />
            );
          })}
        </div>

        <div
          ref={scrollerRef}
          className="flex snap-x snap-mandatory gap-x-4 overflow-x-auto overscroll-x-contain pb-2 pt-4 [scrollbar-width:none] [-webkit-overflow-scrolling:touch] [&::-webkit-scrollbar]:hidden max-sm:-mr-4 sm:grid sm:snap-none sm:grid-cols-2 sm:grid-rows-[repeat(12,auto)] sm:gap-x-4 sm:overflow-visible sm:overscroll-auto sm:px-0 lg:gap-x-5"
          role="region"
          aria-roledescription="carousel"
          aria-label="Investor plans"
        >
          {PLAN_IDS.map((planId, index) => (
            <div
              key={planId}
              className="w-[calc(100%-3.5rem)] shrink-0 snap-start sm:contents"
              aria-label={INVESTOR_PRICING_PLANS[planId].name}
              aria-roledescription="slide"
              data-plan-slide={index}
            >
              <PlanCard
                planId={planId}
                hideSuccessFee={hideFee}
                hideTopSelect={hideFee}
                onSelect={() => onSelectPlan(planId)}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default InvestorPricingPlans;
